/**
 * Local-calendar-date helpers.
 *
 * Two bugs live at the same root cause -- treating a wall-clock calendar
 * date as if it were a UTC one, or vice versa -- and this module exists so
 * neither gets reintroduced as an eighth inline copy.
 *
 * 1. "Today" via UTC. `new Date().toISOString().split('T')[0]` returns the
 *    UTC calendar date, not the viewer's local one. Every BuildGravity
 *    tenant is west of UTC (Pacific to Atlantic), so local time is BEHIND
 *    UTC -- once UTC crosses midnight (~5pm Pacific, ~9pm Atlantic) while
 *    it's still "today" locally, any date-only default computed this way
 *    silently jumps to tomorrow. The v0.17 preview walk hit this exact bug
 *    creating an invoice at 21:12 local (Mountain time) on 2026-09-13: it
 *    defaulted the Invoice Date to 2026-09-14.
 *
 * 2. The inverse: `new Date('2026-09-14')` parses a date-only string as UTC
 *    MIDNIGHT. Displaying or comparing that Date object in a local
 *    (west-of-UTC) timezone reads back as 2026-09-13 -- one calendar day
 *    early. Same confusion, opposite direction.
 *
 * Every function below does its arithmetic in LOCAL wall-clock components
 * (getFullYear/getMonth/getDate/setDate) and never round-trips through
 * toISOString or a bare `new Date(dateOnlyString)`. Calendar-day addition
 * uses setDate (calendar arithmetic), not millisecond addition -- adding
 * N*24h in milliseconds drifts by an hour whenever the range crosses a
 * DST transition (e.g. the Nov 1 fall-back), which can shift the computed
 * date by a full day.
 */

/** Format a Date's LOCAL calendar date as 'YYYY-MM-DD'. */
export function formatLocalDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Today's calendar date in the viewer's local timezone, as 'YYYY-MM-DD'.
 * Pass an explicit Date only for tests -- production callers should call
 * this with no argument so it reads the real current instant.
 */
export function todayLocalDateString(now: Date = new Date()): string {
  return formatLocalDate(now);
}

/**
 * Parse a 'YYYY-MM-DD' string (or a full ISO timestamp -- only the first 10
 * characters are used) as LOCAL MIDNIGHT, never UTC midnight. Returns null
 * for malformed input instead of an "Invalid Date" the caller has to guard.
 *
 * Mirrors the numeric-component construction already audited in
 * `formatDateOnly` (src/lib/utils.ts, 2026-05-30) rather than string
 * concatenation ("dateStr + 'T00:00:00'") -- equivalent result, one fewer
 * string-parsing edge to trust.
 */
export function parseLocalDateString(dateStr: string | null | undefined): Date | null {
  if (!dateStr || typeof dateStr !== 'string') return null;
  const datePart = dateStr.slice(0, 10);
  const parts = datePart.split('-');
  if (parts.length !== 3) return null;
  const [y, m, d] = parts.map(Number);
  if (!Number.isInteger(y) || !Number.isInteger(m) || !Number.isInteger(d)) return null;
  if (m < 1 || m > 12 || d < 1 || d > 31) return null;
  const dt = new Date(y, m - 1, d);
  if (Number.isNaN(dt.getTime())) return null;
  return dt;
}

/**
 * Add N calendar days to a 'YYYY-MM-DD' date string, returning the result as
 * 'YYYY-MM-DD' -- calendar arithmetic (Date#setDate), not millisecond
 * addition, so it's correct across DST transitions and month/year
 * rollovers. `days` may be negative. Returns null if `dateStr` doesn't
 * parse.
 */
export function addLocalDays(dateStr: string, days: number): string | null {
  const dt = parseLocalDateString(dateStr);
  if (!dt) return null;
  dt.setDate(dt.getDate() + days);
  return formatLocalDate(dt);
}
